import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StaffMember, StaffPaginatedResponse } from '../models/StaffMember';
import { PayrollSummary } from '../models/Payroll';
import { StaffComplete } from '../models/StaffComplete';
import { StaffEditPayload } from '../models/StaffEdit';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';
import { PaginationParams } from '../shared/interfaces/pagination.interface';

@Injectable({
  providedIn: 'root'
})
export class StaffService {
  private readonly baseUrl = environment.microservices.staff;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) { }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  /**
   * Atualiza salário de um funcionário (aumento individual).
   * Endpoint: {{baseUrl}}/staff/{{id}}/update-salary
   */
  updateStaffSalary(
    id: number,
    payload: {
      baseSalary: number;
      grossSalary: number;
      netSalary: number;
      currency: string;
      salaryType: string;
      effectiveDate: string;
      isActive: boolean;
      reason?: string;
      notes?: string;
    }
  ): Observable<string> {
    const url = `${this.baseUrl}/staff/${id}/update-salary`;
    // Backend retorna uma mensagem de texto ("Salário atualizado com sucesso")
    // Usamos responseType: 'text' para evitar erro de parse de JSON com status 200.
    return this.http.post(url, payload, {
      headers: this.getAuthHeaders(),
      responseType: 'text'
    }).pipe(
      catchError(err => {
        // Deixa o componente decidir o que mostrar em caso de erro
        return throwError(() => err);
      })
    );
  }

  /**
   * Aplica dissídio coletivo para funcionários CLT.
   * Endpoint: {{baseUrl}}/staff/dissidio
   */
  applyDissidio(payload: { percentage: number; effectiveDate: string }): Observable<{
    percentage: number;
    effectiveDate: string;
    reason: string | null;
    totalEmployees: number;
    updatedEmployees: number;
    skippedEmployees: number;
    totalPreviousSalary: number;
    totalNewSalary: number;
    totalIncrease: number;
    details: {
      staffId: number;
      staffName: string;
      registration: string;
      contractType: string;
      previousSalary: number;
      newSalary: number;
      increase: number;
      updated: boolean;
      reason: string | null;
    }[];
  }> {
    const url = `${this.baseUrl}/staff/dissidio`;
    return this.http.post<any>(url, payload, { headers: this.getAuthHeaders() }).pipe(
      map((res: any) => {
        const detailsRaw = res?.Details || res?.details || [];
        const details = Array.isArray(detailsRaw)
          ? detailsRaw.map((d: any) => ({
              staffId: d.StaffId ?? d.staffId ?? 0,
              staffName: d.StaffName ?? d.staffName ?? '',
              registration: d.Registration ?? d.registration ?? '',
              contractType: d.ContractType ?? d.contractType ?? '',
              previousSalary: d.PreviousSalary ?? d.previousSalary ?? 0,
              newSalary: d.NewSalary ?? d.newSalary ?? 0,
              increase: d.Increase ?? d.increase ?? 0,
              updated: d.Updated ?? d.updated ?? false,
              reason: d.Reason ?? d.reason ?? null
            }))
          : [];

        return {
          percentage: res.Percentage ?? res.percentage ?? payload.percentage,
          effectiveDate: res.EffectiveDate ?? res.effectiveDate ?? payload.effectiveDate,
          reason: res.Reason ?? res.reason ?? null,
          totalEmployees: res.TotalEmployees ?? res.totalEmployees ?? 0,
          updatedEmployees: res.UpdatedEmployees ?? res.updatedEmployees ?? 0,
          skippedEmployees: res.SkippedEmployees ?? res.skippedEmployees ?? 0,
          totalPreviousSalary: res.TotalPreviousSalary ?? res.totalPreviousSalary ?? 0,
          totalNewSalary: res.TotalNewSalary ?? res.totalNewSalary ?? 0,
          totalIncrease: res.TotalIncrease ?? res.totalIncrease ?? 0,
          details
        };
      }),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao aplicar dissídio nos funcionários CLT');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca o salário atual de um funcionário.
   * Endpoint: {{baseUrl}}/staff/{{id}}/current-salary
   */
  getCurrentStaffSalary(id: number): Observable<{
    baseSalary: number;
    grossSalary: number;
    netSalary: number;
    currency: string;
    salaryType: string;
    effectiveDate: string;
    isActive: boolean;
    reason?: string | null;
    notes?: string | null;
  } | null> {
    const url = `${this.baseUrl}/staff/${id}/current-salary`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() }).pipe(
      map((res: any) => {
        const salary = res?.Salary || res?.salary || null;
        if (!salary) {
          return null;
        }
        return {
          baseSalary: salary.BaseSalary ?? salary.baseSalary ?? 0,
          grossSalary: salary.GrossSalary ?? salary.grossSalary ?? 0,
          netSalary: salary.NetSalary ?? salary.netSalary ?? 0,
          currency: salary.Currency ?? salary.currency ?? 'BRL',
          salaryType: salary.SalaryType ?? salary.salaryType ?? 'Mensal',
          effectiveDate: salary.EffectiveDate ?? salary.effectiveDate ?? '',
          isActive: salary.IsActive ?? salary.isActive ?? true,
          reason: salary.Reason ?? salary.reason ?? null,
          notes: salary.Notes ?? salary.notes ?? null
        };
      }),
      catchError(err => {
        // Não bloquear a tela se não tiver salário atual; apenas mostrar erro genérico
        this.errorService.showErrorMessage('Erro ao carregar salário atual do funcionário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca a folha salarial (payroll) de um mês/ano específicos.
   * Endpoint: {{baseUrl}}/staff/payroll/{{month}}/{{year}}
   */
  getPayroll(month: number, year: number): Observable<PayrollSummary> {
    const url = `${this.baseUrl}/staff/payroll/${month}/${year}`;
    return this.http.get<PayrollSummary>(url, { headers: this.getAuthHeaders() }).pipe(
      catchError((err) => {
        this.errorService.showErrorMessage('Erro ao carregar folha salarial');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os funcionários
   */
  getStaff(): Observable<StaffMember[]> {
    const params = new HttpParams()
      .set('page', '1')
      .set('pageSize', '1000');

    return this.http.get<any>(`${this.baseUrl}/staff/list`, { params, headers: this.getAuthHeaders() })
      .pipe(
        map((res: any) => {
          const staff = res?.staff ?? res?.Staff ?? res?.data ?? res?.Data ?? [];
          return Array.isArray(staff) ? staff : [];
        }),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar funcionários');
          return throwError(() => err);
        })
      );
  }

  /**
   * Busca funcionários com paginação
   */
  getStaffPaginated(params: PaginationParams): Observable<StaffPaginatedResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString());

    // Usar 'filter' ao invés de 'search' conforme novo endpoint
    if (params.search) {
      httpParams = httpParams.set('filter', params.search);
    }

    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }

    if (params.sortDirection) {
      // Backend espera sortOrder, mas mantemos sortDirection por compatibilidade
      httpParams = httpParams
        .set('sortOrder', params.sortDirection)
        .set('sortDirection', params.sortDirection);
    }

    if (params.department != null && params.department !== '') {
      httpParams = httpParams.set('department', params.department);
    }

    if (params.status != null && params.status !== '') {
      httpParams = httpParams.set('status', params.status);
    }

    const url = `${this.baseUrl}/staff/list`;
    return this.http.get<any>(url, { params: httpParams, headers: this.getAuthHeaders() }).pipe(
      map((res: any) => this.normalizeStaffPaginatedResponse(res)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar funcionários paginados');
        return throwError(() => err);
      })
    );
  }

  /**
   * Lista funcionários por status (active, inactive ou blocked).
   * Endpoint: {{baseUrl}}/staff/status/{{status}}?page=1&pageSize=10
   */
  getStaffByStatus(
    status: 'active' | 'inactive' | 'blocked',
    params: PaginationParams
  ): Observable<StaffPaginatedResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString());

    if (params.search) {
      httpParams = httpParams.set('filter', params.search);
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams
        .set('sortOrder', params.sortDirection)
        .set('sortDirection', params.sortDirection);
    }
    if (params.department != null && params.department !== '') {
      httpParams = httpParams.set('department', params.department);
    }

    const url = `${this.baseUrl}/staff/status/${status}`;
    return this.http.get<any>(url, { params: httpParams, headers: this.getAuthHeaders() }).pipe(
      map((res: any) => this.normalizeStaffPaginatedResponse(res)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar funcionários por status');
        return throwError(() => err);
      })
    );
  }

  /**
   * Lista funcionários por departamento (paginado).
   * Endpoint: {{baseUrl}}/staff/filter/department/{{departmentId}}?page=1&pageSize=10
   * @param departmentId ID do departamento (não o nome).
   */
  getStaffByDepartment(
    departmentId: string,
    params: PaginationParams
  ): Observable<StaffPaginatedResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString());

    if (params.search) {
      httpParams = httpParams.set('filter', params.search);
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams
        .set('sortOrder', params.sortDirection)
        .set('sortDirection', params.sortDirection);
    }

    const encodedId = encodeURIComponent(departmentId);
    const url = `${this.baseUrl}/staff/filter/department/${encodedId}`;
    return this.http.get<any>(url, { params: httpParams, headers: this.getAuthHeaders() }).pipe(
      map((res: any) => this.normalizeStaffPaginatedResponse(res)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar funcionários por departamento');
        return throwError(() => err);
      })
    );
  }

  /** Normaliza resposta da API para StaffPaginatedResponse (staff, totalCount, page, pageSize, totalPages). */
  private normalizeStaffPaginatedResponse(res: any): StaffPaginatedResponse {
    const staff = res?.staff ?? res?.Staff ?? res?.data ?? res?.Data ?? [];
    const list = Array.isArray(staff) ? staff : [];
    return {
      success: res?.success ?? true,
      page: res?.page ?? 1,
      pageSize: res?.pageSize ?? 10,
      totalCount: res?.totalCount ?? res?.total ?? list.length,
      totalPages: res?.totalPages ?? 1,
      staff: list,
      timestamp: res?.timestamp
    };
  }

  /** Normaliza resposta da API para um único StaffMember (aceita { Staff: {...} } e variações). */
  private normalizeStaffItemResponse(res: any): StaffMember {
    const candidate = res?.Staff ?? res?.staff ?? res?.data ?? res?.Data ?? res;
    // Alguns endpoints retornam em camadas: { Staff: { Staff: {...} } }
    const staff = candidate?.Staff ?? candidate?.staff ?? candidate;
    return staff as StaffMember;
  }

  /**
   * Busca um funcionário específico por ID
   */
  getStaffById(id: number, options?: { suppressErrorToast?: boolean }): Observable<StaffMember> {
    return this.http.get<any>(`${this.baseUrl}/staff/${id}`, { headers: this.getAuthHeaders() })
      .pipe(
        map(res => this.normalizeStaffItemResponse(res)),
        catchError(err => {
          if (!options?.suppressErrorToast) {
            this.errorService.showErrorMessage('Erro ao buscar funcionário');
          }
          return throwError(() => err);
        })
      );
  }

  /**
   * Busca dados completos do funcionário (incluindo contatos, endereços, histórico salarial e anexos)
   */
  getStaffComplete(id: number): Observable<StaffComplete> {
    const url = `${this.baseUrl}/staff/${id}/complete`;
    return this.http.get<StaffComplete>(url, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar dados completos do funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Atualiza dados completos do funcionário
   */
  updateStaffComplete(id: number, payload: StaffEditPayload): Observable<StaffComplete> {
    return this.http.put<StaffComplete>(`${this.baseUrl}/staff/complete/${id}`, payload, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao atualizar dados completos do funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Cria novo funcionário com dados completos
   */
  createStaffComplete(payload: StaffEditPayload): Observable<StaffComplete> {
    return this.http.post<StaffComplete>(`${this.baseUrl}/staff/complete`, payload, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao criar funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Cria novo funcionário usando StaffDTO (formato do backend)
   */
  createStaffWithDTO(staffDTO: any): Observable<StaffComplete> {
    return this.http.post<StaffComplete>(`${this.baseUrl}/staff/create`, staffDTO, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Cria um novo funcionário
   */
  createStaff(staff: Partial<StaffMember>): Observable<StaffMember> {
    return this.http.post<StaffMember>(`${this.baseUrl}/staff`, staff, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao criar funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Atualiza um funcionário existente
   */
  updateStaff(id: number, staff: Partial<StaffMember>): Observable<StaffMember> {
    return this.http.put<StaffMember>(`${this.baseUrl}/staff/${id}`, staff, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao atualizar funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Remove um funcionário
   */
  deleteStaff(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/staff/${id}`, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao excluir funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Upload de documento para o blob storage
   */
  uploadDocument(file: File, staffId: number, documentType: string, documentName?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    // Parâmetros da URL conforme especificado
    let params = new HttpParams()
      .set('staffId', staffId.toString())
      .set('fileType', 'document')
      .set('documentType', documentType)
      .set('category', 'documentos');
    
    // Se tiver nome do documento, adicionar
    if (documentName) {
      params = params.set('documentName', documentName);
    }
    
    return this.http.post(`${this.baseUrl}/staff/blob/upload`, formData, { params, headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao fazer upload do documento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Deletar documento do blob storage
   */
  deleteDocument(staffId: string, fileName: string): Observable<any> {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('fileName', fileName);
    
    return this.http.delete(`${this.baseUrl}/staff/blob/delete-document`, { params, headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao excluir documento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Download de documento do blob storage
   */
  downloadDocument(staffId: string, fileName: string): Observable<Blob> {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('fileName', fileName);
    
    return this.http.get(`${this.baseUrl}/staff/blob/download-document`, { 
      params,
      responseType: 'blob',
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao baixar documento');
        return throwError(() => err);
      })
    );
  }

  /**
   * Registra demissão/desligamento do funcionário (endpoint API: POST .../staff/{id}/deactivate).
   */
  terminateStaff(id: number, terminationDate: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/staff/${id}/deactivate`, { terminationDate }, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao registrar demissão');
          return throwError(() => err);
        })
      );
  }

  /**
   * Bloquear funcionário
   */
  blockStaff(id: number, blockPeriod: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/staff/${id}/block`, { blockPeriod }, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao bloquear funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Desbloquear funcionário
   */
  unblockStaff(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/staff/${id}/unblock`, {}, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao desbloquear funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Reativar funcionário
   */
  reactivateStaff(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/staff/${id}/reactivate`, {}, { headers: this.getAuthHeaders() })
      .pipe(
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao reativar funcionário');
          return throwError(() => err);
        })
      );
  }

  /**
   * Exporta funcionários para Excel
   * @param exportData Dados de exportação (fields, filters, options)
   * @returns Observable com o arquivo Excel (Blob)
   */
  exportStaffToExcel(exportData: {
    fields: string[];
    filters?: {
      department?: string;
      isActive?: boolean;
      contractType?: string;
    };
    options?: {
      includeContacts?: boolean;
      includeAddresses?: boolean;
      includeSalary?: boolean;
      includeBenefits?: boolean;
    };
  }): Observable<Blob> {
    const url = `${this.baseUrl}/staff/export/excel`;
    
    return this.http.post(url, exportData, {
      responseType: 'blob',
      observe: 'response',
      headers: this.getAuthHeaders()
    }).pipe(
      map(response => {
        // Extrair o blob da resposta
        const blob = response.body;
        if (!blob) {
          throw new Error('Resposta vazia do servidor');
        }
        
        // Verificar o Content-Type da resposta
        const contentType = response.headers.get('content-type') || 
                           'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        
        // Garantir que o blob tenha o tipo MIME correto para Excel (.xlsx)
        // O arquivo binário Excel precisa ter o tipo correto para ser reconhecido pelo navegador
        if (!blob.type || !blob.type.includes('spreadsheet')) {
          return new Blob([blob], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
        }
        
        return blob;
      }),
      catchError(err => {
        // Se o erro vier como blob (alguns servidores retornam erro como blob)
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            try {
              const errorText = JSON.parse(reader.result as string);
              this.errorService.showErrorMessage(errorText.message || 'Erro ao gerar relatório Excel');
            } catch {
              this.errorService.showErrorMessage('Erro ao gerar relatório Excel');
            }
          };
          reader.readAsText(err.error);
        } else {
          this.errorService.showErrorMessage('Erro ao gerar relatório Excel');
        }
        return throwError(() => err);
      })
    );
  }
}
